import { z } from 'zod';

import { positiveIntSchema, uuidSchema } from '../common/primitives';

/**
 * Auf welche Storage Box kommt das Repository einer neuen Site?
 *
 * Derselbe Aufbau wie bei der Platzierung eines Nodes
 * (`scheduling/placement.ts`) und aus demselben Grund: Diese Funktion
 * entscheidet, wo Kundendaten liegen. Sie muss ohne Datenbank, ohne Netzwerk
 * und ohne Nest-Kontext gegen konstruierte Zustände prüfbar sein.
 *
 *   1. FILTER  — abgeschaltet, zu voll, kennt die Site schon.
 *   2. RANG    — von den übrigen gewinnt die mit dem meisten freien Platz.
 *
 * ## Eine volle Box hält keine Sicherung an
 *
 * Die Auswahl läuft **einmal**, wenn eine Site angelegt wird; danach behält die
 * Site ihre Box. Läuft eine Box voll, nimmt sie nur keine neuen Sites mehr auf.
 * Die Sicherungen der Sites, die schon auf ihr liegen, laufen weiter — dieselbe
 * Haltung wie bei der Quote: Eine erreichte Grenze schaltet nichts ab, sie
 * nimmt nur nichts Neues mehr an.
 */

/** Eingabedaten einer Box. Nur das, was für die Entscheidung zählt. */
export interface StorageBoxCandidate {
  readonly boxId: string;
  readonly label: string;
  readonly enabled: boolean;
  readonly capacityGb: number;
  /** Gemessene Belegung der Box (Ist). */
  readonly usedGb: number;
  /**
   * Summe dessen, was den Sites auf dieser Box bereits zugesagt ist (Soll).
   *
   * Getrennt von `usedGb` geführt, weil beide Zahlen verschiedene Fragen
   * beantworten: Eine frisch angelegte Site belegt noch nichts, ist aber schon
   * versprochen. Wer nur misst, verteilt an einem Abend zwanzig Sites auf eine
   * Box, die keine zwanzig trägt.
   */
  readonly reservedGb: number;
}

export const storageBoxRequestSchema = z.object({
  siteId: uuidSchema,
  /** Der Platzbedarf der Site — dieselbe Zahl, die der Scheduler reserviert. */
  requiredGb: positiveIntSchema,
});

export type StorageBoxRequest = z.infer<typeof storageBoxRequestSchema>;

/**
 * Wie viel Platz ein Repository gemessen an der Site braucht.
 *
 * Ein Faktor und keine Gleichheit: `restic` legt siebzehn Momentaufnahmen ab,
 * dedupliziert sie aber gegeneinander. Bei einer Website, deren Masse aus
 * Bildern besteht, die sich nicht mehr ändern, kostet die zweite Momentaufnahme
 * fast nichts; bei einer, die täglich ihren gesamten Bestand umschreibt, kostet
 * sie noch einmal alles. Der Faktor 2 ist die vorsichtige Mitte und
 * überschreibbar — wer ihn kleiner setzt, tut es ausdrücklich.
 */
export const BACKUP_SPACE_FACTOR = 2;

/**
 * Freier Platz, der auf einer Box übrig bleiben muss.
 *
 * Eine bis zum letzten Gigabyte gefüllte Box ist keine, von der sich noch
 * zurückholen lässt: `restic prune` schreibt um, bevor es freigibt.
 */
export const MIN_FREE_STORAGE_GB = 10;

export interface StorageBoxThresholds {
  readonly spaceFactor: number;
  readonly minFreeGb: number;
}

export const STORAGE_BOX_THRESHOLDS: StorageBoxThresholds = {
  spaceFactor: BACKUP_SPACE_FACTOR,
  minFreeGb: MIN_FREE_STORAGE_GB,
};

/** Gründe, aus denen eine Box ausgeschlossen wurde. */
export const BOX_REJECTION_REASONS = {
  disabled: 'disabled',
  insufficientFreeSpace: 'insufficient-free-space',
  belowFreeSpaceFloor: 'below-free-space-floor',
} as const;

export type BoxRejectionReason = (typeof BOX_REJECTION_REASONS)[keyof typeof BOX_REJECTION_REASONS];

/** Menschenlesbare Begründungen — werden 1:1 in Logs und API-Fehler übernommen. */
export const BOX_REJECTION_REASON_MESSAGES: Record<BoxRejectionReason, string> = {
  [BOX_REJECTION_REASONS.disabled]:
    'Storage Box ist abgeschaltet. Die Sicherungen der Sites, die schon auf ihr liegen, laufen weiter; für neue Sites einschalten über PATCH /storage-boxes/<id>.',
  [BOX_REJECTION_REASONS.insufficientFreeSpace]:
    'Freier Platz der Storage Box reicht für diese Site nicht aus. Box vergrößern oder eine weitere eintragen über POST /storage-boxes.',
  [BOX_REJECTION_REASONS.belowFreeSpaceFloor]:
    'Storage Box unterschreitet den Mindest-Restplatz und nimmt deshalb keine neuen Sites mehr auf. Alte Repositories entfernter Sites abräumen oder eine weitere Box eintragen.',
};

export interface RejectedStorageBox {
  readonly boxId: string;
  readonly label: string;
  readonly reason: BoxRejectionReason;
  readonly message: string;
}

export interface RankedStorageBox {
  readonly boxId: string;
  readonly label: string;
  /** Freier Platz nach Abzug des bereits Zugesagten. Mehr ist besser. */
  readonly freeGb: number;
}

export interface StorageBoxSelection {
  readonly selected: RankedStorageBox | null;
  readonly ranked: readonly RankedStorageBox[];
  readonly rejected: readonly RejectedStorageBox[];
}

/**
 * Freier Platz einer Box.
 *
 * Es gilt die **strengere** der beiden Zahlen: gemessene Belegung oder
 * zugesagte Reservierung. Die Alternative wäre, dem Ist zu glauben — und damit
 * genau in der Nacht zu überbuchen, in der die zwanzig frisch angelegten Sites
 * ihre erste Sicherung schreiben.
 */
export function freeStorageGb(candidate: StorageBoxCandidate): number {
  const occupied = Math.max(candidate.usedGb, candidate.reservedGb);

  return Math.max(0, candidate.capacityGb - occupied);
}

/**
 * Stufe 1 — Filter.
 * Gibt entweder `null` (Box besteht) oder den Ablehnungsgrund zurück.
 */
export function filterStorageBox(
  candidate: StorageBoxCandidate,
  request: StorageBoxRequest,
  thresholds: StorageBoxThresholds = STORAGE_BOX_THRESHOLDS,
): BoxRejectionReason | null {
  if (!candidate.enabled) {
    return BOX_REJECTION_REASONS.disabled;
  }

  const free = freeStorageGb(candidate);

  // Der Mindest-Restplatz zuerst: Eine Box, die ohnehin dichtmacht, soll das
  // auch dann melden, wenn die angefragte Site winzig ist. Andernfalls stünde
  // in der Ablehnung "reicht für diese Site nicht", und der Betreiber suchte
  // den Fehler bei der Site statt bei der Box.
  if (free <= thresholds.minFreeGb) {
    return BOX_REJECTION_REASONS.belowFreeSpaceFloor;
  }

  if (free - request.requiredGb * thresholds.spaceFactor < thresholds.minFreeGb) {
    return BOX_REJECTION_REASONS.insufficientFreeSpace;
  }

  return null;
}

/**
 * Führt Filter und Rangfolge aus und liefert das vollständige Ergebnis.
 *
 * Auch die abgelehnten Boxen kommen zurück. Das ist der Unterschied zwischen
 * "keine Box verfügbar" und einer Meldung, mit der sich etwas anfangen lässt:
 * Der Betreiber sieht, ob alle Boxen voll sind oder ob jemand sie abgeschaltet
 * und es vergessen hat.
 */
export function selectStorageBox(
  candidates: readonly StorageBoxCandidate[],
  request: StorageBoxRequest,
  thresholds: StorageBoxThresholds = STORAGE_BOX_THRESHOLDS,
): StorageBoxSelection {
  const rejected: RejectedStorageBox[] = [];
  const eligible: RankedStorageBox[] = [];

  for (const candidate of candidates) {
    const reason = filterStorageBox(candidate, request, thresholds);

    if (reason === null) {
      eligible.push({
        boxId: candidate.boxId,
        label: candidate.label,
        freeGb: freeStorageGb(candidate),
      });
    } else {
      rejected.push({
        boxId: candidate.boxId,
        label: candidate.label,
        reason,
        message: BOX_REJECTION_REASON_MESSAGES[reason],
      });
    }
  }

  // Bei Gleichstand entscheidet die Box-ID. Damit ist die Auswahl vollständig
  // deterministisch und in Tests reproduzierbar.
  const ranked = eligible.toSorted((a, b) =>
    b.freeGb === a.freeGb ? a.boxId.localeCompare(b.boxId) : b.freeGb - a.freeGb,
  );

  return {
    selected: ranked[0] ?? null,
    ranked,
    rejected,
  };
}
